// src/hooks/useFooterData.js
import { useEffect, useState } from 'react';
import api from 'services/api';

const FOOTER_CACHE_PREFIX = 'footer_data_cache_';

function getCacheKey(scope) {
  return `${FOOTER_CACHE_PREFIX}${scope}`;
}

function readFooterCache(scope) {
  try {
    const raw = sessionStorage.getItem(getCacheKey(scope));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeFooterCache(scope, data) {
  try {
    sessionStorage.setItem(getCacheKey(scope), JSON.stringify(data));
  } catch {
    // sessionStorage plein ou indisponible
  }
}

export function resolveFooterEndpoint(isSubSite, subSiteSlug) {
  if (!subSiteSlug) return '/footer';

  const verified = sessionStorage.getItem(`subsite_verified_${subSiteSlug}`);
  if (verified === 'false') return '/footer';
  if (verified === 'true' || isSubSite) return `/footer/${subSiteSlug}`;

  return '/footer';
}

async function fetchFooterWithFallback(endpoint) {
  try {
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    if (endpoint !== '/footer' && error.response?.status === 404) {
      const fallback = await api.get('/footer');
      return fallback.data;
    }
    throw error;
  }
}

function getScope(isSubSite, subSiteSlug, forcedEndpoint) {
  if (forcedEndpoint?.startsWith('/footer/')) {
    return forcedEndpoint.replace('/footer/', '');
  }
  if (isSubSite && subSiteSlug) return `subsite_${subSiteSlug}`;
  return 'main';
}

function useFooterData(isSubSite, subSiteSlug, options = {}) {
  const { forcedEndpoint } = options;
  const scope = getScope(isSubSite, subSiteSlug, forcedEndpoint);

  const [footerData, setFooterData] = useState(() => (
    readFooterCache(scope) || readFooterCache('main')
  ));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      const endpoint = forcedEndpoint ?? resolveFooterEndpoint(isSubSite, subSiteSlug);

      try {
        const data = await fetchFooterWithFallback(endpoint);
        if (!isMounted) return;
        setFooterData(data);
        writeFooterCache(scope, data);
        if (endpoint === '/footer') {
          writeFooterCache('main', data);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des données du footer:', err);
        const cached = readFooterCache(scope) || readFooterCache('main');
        if (isMounted && cached) {
          setFooterData(cached);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isSubSite, subSiteSlug, forcedEndpoint, scope]);

  return { footerData, loading };
}

export default useFooterData;
